// Markdown is an import and export format only (design §5.1). These two
// functions are the whole of it: source → paragraph texts, and back.
//
// This parser must see exactly what the renderer sees, which is why it knows
// about mathematics too. A $$ block is a fenced construct like ``` — blank
// lines inside it are its own content — and to a parser without remark-math
// it is instead two or three paragraphs, so a formula with a blank line in it
// would be torn into that many editor paragraphs and none of them would
// render. The two parsers disagreeing is the bug; agreeing is the fix.

import type { ListItem, Root, RootContent } from "mdast";
import remarkMath from "remark-math";
import remarkParse from "remark-parse";
import { unified } from "unified";

const parser = unified().use(remarkParse).use(remarkMath);

/** One paragraph per top-level block, except that each list item is its own
 *  paragraph (design §5.1). Texts are sliced from the source verbatim, so
 *  nothing is normalised away. */
export function splitMarkdown(source: string): string[] {
  const tree = parser.parse(source) as Root;
  const out: string[] = [];
  for (const node of tree.children) {
    if (node.type === "list") {
      for (const item of node.children) out.push(slice(source, item));
    } else {
      out.push(slice(source, node));
    }
  }
  return out;
}

function slice(source: string, node: RootContent | ListItem): string {
  const start = node.position?.start.offset;
  const end = node.position?.end.offset;
  return start == null || end == null ? "" : source.slice(start, end);
}

const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s/;

export function isListItem(text: string): boolean {
  return LIST_ITEM.test(text);
}

/** Adjacent list items are joined with a single newline so a tight list stays
 *  tight; everything else gets a blank line. Export is exactly this (§3.7). */
export function joinMarkdown(paragraphs: string[]): string {
  let out = "";
  for (let i = 0; i < paragraphs.length; i++) {
    const text = paragraphs[i] ?? "";
    if (i > 0) {
      const prev = paragraphs[i - 1] ?? "";
      out += isListItem(text) && isListItem(prev) ? "\n" : "\n\n";
    }
    out += text;
  }
  return out;
}
